import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './styles.css';
import boardCertifiedImg from '../../images/star.png';
import infoCircleIcon from '../../images/infoCircle.svg';
import { usePlanFeatures } from '../../contexts/PlanFeaturesContext';
import { formatYesNo } from '../../utils/yesNoDisplay';
import HoverTooltip from '../hoverTooltip/hoverTooltip';
import GroupAffiliationsModal from '../groupAffiliationsModal/groupAffiliationsModal';
import { useModalFocusReturn } from '../../hooks/useModalFocusReturn';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

const renderSpecialties = (providerSpecialty) => {
  if (!providerSpecialty || providerSpecialty.length === 0) {
    return { certified: <span>N/A</span>, notCertified: <span>N/A</span>, all: [] };
  }
//testing
  const certifiedSpecs = [];
  const notCertifiedSpecs = [];
  const allSpecs = [];

  providerSpecialty.forEach((spec, index) => {
    const element = (
      <span key={`${spec.specialtyName}-${index}`} className="specialty-item">
        {spec.specialtyName}
      </span>
    );

    if (spec.boardCertifiedFlag === 'Yes') {
      certifiedSpecs.push(element);
      allSpecs.push({ name: spec.specialtyName, certified: true, index });
    } else {
      notCertifiedSpecs.push(element);
      allSpecs.push({ name: spec.specialtyName, certified: false, index });
    }
  });

  const addCommas = (arr) =>
    arr.reduce((acc, curr, idx) => {
      acc.push(curr);
      if (idx < arr.length - 1) acc.push(<span key={`comma-${idx}`}>, </span>);
      return acc;
    }, []);

  return {
    certified: certifiedSpecs.length > 0 ? addCommas(certifiedSpecs) : <span>N/A</span>,
    notCertified: notCertifiedSpecs.length > 0 ? addCommas(notCertifiedSpecs) : <span>N/A</span>,
    all: allSpecs,
  };
};

// Helper function to convert string to Title Case (Camel Case)
const toTitleCase = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Ethnicity/race values that should be treated as "no data" and displayed as
// "None found" instead of their raw (unhelpful/placeholder) API text.
const UNKNOWN_ETHNICITY_VALUES = [
  'UNKNOWN',
  'I CHOOSE NOT TO ANSWER',
  'NULL',
  'NO ETHNICITY',
  'NOT PROVIDED',
  'NOT APPLICABLE',
];

const getEthnicityDisplay = (ethnicity) => {
  if (!ethnicity) return 'None found';
  // Normalize case (handles UPPER, lower, or Mixed Case values from the API)
  // and collapse extra whitespace before comparing against known placeholders.
  const normalized = ethnicity.trim().replace(/\s+/g, ' ').toUpperCase();
  if (UNKNOWN_ETHNICITY_VALUES.includes(normalized)) return 'None found';
  return toTitleCase(ethnicity);
};


const ProviderDetailsType4 = ({ provider, providerSpecialty, isExpanded = true }) => {
  const { isProviderDetailsFieldEnabled } = usePlanFeatures();
  // Group/Hospital affiliations tooltips should only be reachable by keyboard while
  // this "Provider details" accordion section is actually expanded — see
  // providerDetails.jsx's matching comment for why the shared .expandable-content
  // accordion (max-height:0/overflow:hidden, not display:none) otherwise leaves
  // collapsed content focusable.
  const affiliationTooltipTabIndex = isExpanded ? 0 : -1;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  // "Show all group affiliations" popup — only used when there are more than 3 group
  // affiliations to display (see the group affiliations render below), so the inline
  // text doesn't grow unbounded for providers with many affiliations.
  const [showAllAffiliationsModal, setShowAllAffiliationsModal] = useState(false);
  const [showAllHospitalAffiliationsModal, setShowAllHospitalAffiliationsModal] = useState(false);
  const [showAllSpecialSkillsModal, setShowAllSpecialSkillsModal] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // Restore focus to the "Check specialties" button once its modal closes.
  useModalFocusReturn(isModalOpen);

  // Lock page scroll while open, without jumping to the top and correctly restoring
  // the exact scroll position on close.
  useBodyScrollLock(isModalOpen);

  // Move focus into the modal on open (its close button), trap Tab within it while
  // open, and close on Escape. This modal previously had none of these — unlike the
  // dedicated <Modal>-style components (e.g. languageModal.jsx) — which is why focus
  // never visibly moved anywhere when it opened. Declared after useModalFocusReturn
  // so that hook captures the real outside trigger before this one steals focus.
  useEffect(() => {
    if (!isModalOpen) return undefined;

    closeButtonRef.current?.focus({ preventScroll: true });

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeModal();
        return;
      }
      if (e.key === 'Tab') {
        const focusableElements = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements && focusableElements.length > 0) {
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen]);

  const specialties = renderSpecialties(providerSpecialty);

  return (
    <div className="frameDetail frameDetail-type4">
      <div className="div-2">
        <div className="div-3">
          <div className="div-4">
            {isProviderDetailsFieldEnabled('ethnicity') ? (
              // When ethnicity is enabled, follow the requested div-10/p.p layout
              // (matching the Provider ID/NPI/state license row's markup pattern)
              // instead of the default gender-specialties-row layout below.
              <div className="div-10">
                <p className="p">
                  {specialties.all.length > 0 ? (
                    <button
                      onClick={openModal}
                      className="check-specialties-link"
                      aria-label="Check all specialties"
                      tabIndex={affiliationTooltipTabIndex}
                    >
                      <span>Check specialties</span>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" className="chevron-icon">
                        <path d="M4.5 2L8.5 6L4.5 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  ) : (
                    <span className="no-specialties-found"><strong>Specialties</strong>: None found</span>
                  )}
                </p>

                {isProviderDetailsFieldEnabled('gender') && (
                  <p className="p">
                    <p className="gender-male">
                      <span className="text-wrapper">Provider Gender: </span>
                      <span className="span">{provider?.provSex === 'F' ? 'Female' : 'Male'}</span>
                    </p>
                  </p>
                )}

                <p className="p">
                  <p className="ethnicity">
                    <span className="text-wrapper">Provider race/ethnicity: </span>
                    <span className="span">{getEthnicityDisplay(provider?.ethnicity)}</span>
                  </p>
                </p>
              </div>
            ) : (
              <div className="gender-specialties-row">
                 {specialties.all.length > 0 ? (
                   <div className="check-specialties-wrapper">
                    <button 
                      onClick={openModal}
                      className="check-specialties-link"
                      aria-label="Check all specialties"
                      tabIndex={affiliationTooltipTabIndex}
                    >
                      <span>Check specialties</span>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" className="chevron-icon">
                        <path d="M4.5 2L8.5 6L4.5 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                 ) : (
                   <div className="check-specialties-wrapper">
                     <span className="no-specialties-found"><strong>Specialties</strong>: None found</span>
                   </div>
                 )}
                {isProviderDetailsFieldEnabled('gender') && (
                  <div className="gender-specialties-cell">
                    <p className="gender-male">
                      <span className="text-wrapper">Provider Gender: </span>
                      <span className="span">{provider?.provSex === 'F' ? 'Female' : 'Male'}</span>
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="card-section-divider" role="separator" aria-hidden="true" />

          <div className="group">
            <div className="div-8">
              {isProviderDetailsFieldEnabled('groupAffiliations') && (
                <div className="div-9">
                  <div className="label">
                    <div className="text-wrapper-2">Group affiliations</div>

                    <HoverTooltip
                      heading="Group affiliations"
                      body="Shows what practice or group a provider takes part in. They can provide care as part of the group. They also share practice resources."
                      tabIndex={affiliationTooltipTabIndex}
                    >
                      <img src={infoCircleIcon} alt="" style={{ width: '24px', height: '24px', cursor: 'help' }} />
                    </HoverTooltip>
                  </div>

                  <p className="text-wrapper-3">
                    {(() => {
                        const groupAffiliationNames = provider?.affiliations && provider.affiliations.length > 0
                            ? provider.affiliations
                                .filter(aff => aff.affilType === 'GROUP' || aff.affilType === 'DIRECT')
                                .map(aff => aff.affiliateName)
                                .filter(Boolean)
                            : [];

                        if (groupAffiliationNames.length === 0) {
                            return 'None found';
                        }

                        if (groupAffiliationNames.length > 3) {
                            return (
                                <>
                                    {groupAffiliationNames.slice(0, 3).join(', ')}{', '}
                                    <button
                                        type="button"
                                        className="show-all-affiliations-link"
                                        onClick={() => setShowAllAffiliationsModal(true)}
                                        tabIndex={affiliationTooltipTabIndex}
                                    >
                                        Show all group affiliations
                                    </button>
                                    <GroupAffiliationsModal
                                        isOpen={showAllAffiliationsModal}
                                        onClose={() => setShowAllAffiliationsModal(false)}
                                        affiliations={groupAffiliationNames}
                                        title="Group affiliations"
                                    />
                                </>
                            );
                        }

                        return groupAffiliationNames.join(', ');
                    })()}
                  </p>
                </div>
              )}

              {isProviderDetailsFieldEnabled('hospitalAffiliations') && (
                <div className="div-9">
                  <div className="label">
                    <div className="text-wrapper-2">Hospital affiliations</div>

                    <HoverTooltip
                      heading="Hospital affiliations"
                      body="Shows what hospital network a provider or group takes part in. They provide care as part of the network. They also share network resources."
                      tabIndex={affiliationTooltipTabIndex}
                    >
                      <img src={infoCircleIcon} alt="" style={{ width: '24px', height: '24px', cursor: 'help' }} />
                    </HoverTooltip>
                  </div>

                  <p className="text-wrapper-3">
                    {(() => {
                        const hospitalAffiliationNames = provider?.affiliations && provider.affiliations.length > 0
                            ? provider.affiliations
                                .filter(aff => aff.affilType === 'HOSP STAFF')
                                .map(aff => aff.affiliateName)
                                .filter(Boolean)
                            : [];

                        if (hospitalAffiliationNames.length === 0) {
                            return 'None found';
                        }

                        if (hospitalAffiliationNames.length > 3) {
                            return (
                                <>
                                    {hospitalAffiliationNames.slice(0, 3).join(', ')}{', '}
                                    <button
                                        type="button"
                                        className="show-all-affiliations-link"
                                        onClick={() => setShowAllHospitalAffiliationsModal(true)}
                                        tabIndex={affiliationTooltipTabIndex}
                                    >
                                        Show all hospital affiliations
                                    </button>
                                    <GroupAffiliationsModal
                                        isOpen={showAllHospitalAffiliationsModal}
                                        onClose={() => setShowAllHospitalAffiliationsModal(false)}
                                        affiliations={hospitalAffiliationNames}
                                        title="Hospital affiliations"
                                    />
                                </>
                            );
                        }

                        return hospitalAffiliationNames.join(', ');
                    })()}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="card-section-divider" role="separator" aria-hidden="true" />

          {isProviderDetailsFieldEnabled('specialTraining') && (
            <div className="group-wrapper">
              <div className="group-2">
                <p className="special-training">
                  <span className="text-wrapper">Special skills:</span>

                  <span className="span">
                    {' '}
                    {(() => {
                        const specialSkillNames = typeof provider?.specialTraining === 'string' && provider.specialTraining.trim().length > 0
                            ? provider.specialTraining.split(',').map(s => s.trim()).filter(Boolean)
                            : [];

                        if (specialSkillNames.length === 0) {
                            return 'None found';
                        }

                        if (specialSkillNames.length > 3) {
                            return (
                                <>
                                    {specialSkillNames.slice(0, 3).join(', ')}{', '}
                                    <button
                                        type="button"
                                        className="show-all-affiliations-link"
                                        onClick={() => setShowAllSpecialSkillsModal(true)}
                                        tabIndex={affiliationTooltipTabIndex}
                                    >
                                        Show all special skills
                                    </button>
                                    <GroupAffiliationsModal
                                        isOpen={showAllSpecialSkillsModal}
                                        onClose={() => setShowAllSpecialSkillsModal(false)}
                                        affiliations={specialSkillNames}
                                        title="Special skills"
                                    />
                                </>
                            );
                        }

                        return specialSkillNames.join(', ');
                    })()}
                  </span>
                </p>
              </div>
            </div>
          )}

          <div className="card-section-divider" role="separator" aria-hidden="true" />

          {isProviderDetailsFieldEnabled('cshcsProvider') && (
            <div className="group-wrapper cshcs-wrapper">
              <div className="group-2">
                <p className="cshcs-provider">
                  <span className="text-wrapper">Children&rsquo;s special health care services (CSHCS) provider:</span>

                  <span className="span">
                    {' '}
                    {formatYesNo(provider?.cshcsProvider)}
                  </span>
                </p>
              </div>
            </div>
          )}

          <div className="card-section-divider" role="separator" aria-hidden="true" />
        </div>
        <div className="div-10">
          {isProviderDetailsFieldEnabled('provId') && (
            <p className="p">
              <span className="text-wrapper">Provider ID: </span>
              <span className="span"  translate='no'>{(provider?.provId && provider.provId !== '') || (provider?.providerId && provider.providerId !== '') ? (provider.provId || provider.providerId) : 'None found'}</span>
            </p>
          )}

          {isProviderDetailsFieldEnabled('npi') && (
            <p className="p">
              <span className="text-wrapper">NPI ID: </span>
              <span className="span"  translate='no'>{provider?.npi && provider.npi !== '' ? provider.npi : 'None found'}</span>
            </p>
          )}

          {isProviderDetailsFieldEnabled('stateLicenseNumber') && (
            <p className="p">
              <span className="text-wrapper">State license number:</span>
              <span className="span"  translate='no'>{(provider?.stateLicenseNumber && provider.stateLicenseNumber !== '') ? provider.stateLicenseNumber : 'None found'}</span>
            </p>
          )}
                    
        </div>
      </div>

      {isModalOpen && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={closeModal} role="presentation">
          <div
            className="modal-content"
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`specialties-modal-title-${provider?.provId || provider?.providerId || 'type4'}`}
          >
            <div className="modal-header">
              <h2 id={`specialties-modal-title-${provider?.provId || provider?.providerId || 'type4'}`} className="modal-title">All Specialties</h2>
              <button ref={closeButtonRef} className="modal-close-button" onClick={closeModal} aria-label="Close modal">×</button>
            </div>
            <div className="modal-body">
              <div className="modal-section">
                <div className="modal-specialties-combined">
                  {specialties.all.length > 0 ? (
                    <ul className="specialties-list">
                      {specialties.all.map((spec, idx) => (
                        <li key={`specialty-${idx}`} className="specialty-list-item">
                          {spec.certified && (
                            <img className="specialty-star-icon" src={boardCertifiedImg} alt="Board certified" />
                          )}
                          <span className={spec.certified ? 'specialty-name certified' : 'specialty-name'}>
                            {spec.name}
                          </span>
                          {spec.certified && (
                            <span className="certified-badge">
                              Board Certified
                              <HoverTooltip
                                heading="Board certified"
                                body="This provider passed special tests in their field of health care."
                              />
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="no-specialties">No specialties available</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ProviderDetailsType4;
